"use client";

import { useState, useEffect, useRef } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────
interface CurrentPower {
  gpu_w: number;
  cpu_w: number;
  base_w: number;
  total_w: number;
  cpu_pct: number;
}

interface ChartBar {
  date?: string;
  month?: string;
  kwh: number;
  co2e_g: number;
}

interface PowerSummary {
  current: CurrentPower;
  today_kwh: number;
  today_co2e_g: number;
  month_kwh: number;
  month_co2e_g: number;
  year_kwh: number;
  year_co2e_g: number;
  co2_factor_g_per_kwh: number;
  daily_chart: ChartBar[];
  monthly_chart: ChartBar[];
}

// ─── SVG Bar Chart ────────────────────────────────────────────────────────────
function BarChart({
  bars,
  labelKey,
  valueKey,
  color,
  unit,
  maxBars = 30,
}: {
  bars: ChartBar[];
  labelKey: "date" | "month";
  valueKey: "kwh" | "co2e_g";
  color: string;
  unit: string;
  maxBars?: number;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const data = bars.slice(-maxBars);
  const W = 600;
  const H = 80;
  const pad = { l: 4, r: 4, t: 8, b: 18 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const maxVal = Math.max(...data.map((d) => d[valueKey]), 0.0001);
  const bw = innerW / Math.max(data.length, 1);
  const gap = bw * 0.18;

  const fmt = (v: number) =>
    valueKey === "kwh"
      ? v < 1 ? `${(v * 1000).toFixed(0)} Wh` : `${v.toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kWh`
      : v < 1000 ? `${v.toFixed(0)} g` : `${(v / 1000).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;

  const shortLabel = (bar: ChartBar) => {
    const raw = bar[labelKey] ?? "";
    if (labelKey === "date") return raw.slice(5);          // MM-DD
    return raw.slice(5);                                   // MM
  };

  return (
    <div className="relative w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 260 }}>
        {data.map((bar, i) => {
          const bh = Math.max((bar[valueKey] / maxVal) * innerH, 2);
          const x = pad.l + i * bw;
          const y = pad.t + innerH - bh;
          const isHov = hovered === i;
          return (
            <g key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
              <rect
                x={x + gap / 2}
                y={y}
                width={bw - gap}
                height={bh}
                rx={2}
                fill={color}
                opacity={isHov ? 1 : 0.75}
              />
              {/* label */}
              {(data.length <= 14 || i % Math.ceil(data.length / 14) === 0) && (
                <text
                  x={x + bw / 2}
                  y={H - 3}
                  textAnchor="middle"
                  fontSize={7}
                  fill="#9ca3af"
                >
                  {shortLabel(bar)}
                </text>
              )}
              {/* tooltip */}
              {isHov && (
                <g>
                  <rect
                    x={Math.min(x + bw / 2 - 28, W - 60)}
                    y={y - 22}
                    width={56}
                    height={16}
                    rx={3}
                    fill="#1f2937"
                    opacity={0.95}
                  />
                  <text
                    x={Math.min(x + bw / 2, W - 32)}
                    y={y - 11}
                    textAnchor="middle"
                    fontSize={7.5}
                    fill="#fff"
                  >
                    {shortLabel(bar)}: {fmt(bar[valueKey])}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
      {data.length === 0 && (
        <p className="text-xs text-gray-600 text-center py-4">
          Ingen data endnu — data samles hvert minut.
        </p>
      )}
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({
  label, kwh, co2e_g, showCo2,
}: { label: string; kwh: number; co2e_g: number; showCo2: boolean }) {
  const kwhStr = kwh < 1 ? `${(kwh * 1000).toFixed(0)} Wh` : `${kwh.toLocaleString("da-DK", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kWh`;
  const co2Str = co2e_g < 1000 ? `${co2e_g.toFixed(0)} g` : `${(co2e_g / 1000).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;
  return (
    <div className="bg-gray-800/60 rounded-xl p-3 flex flex-col gap-1 min-w-0">
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
      <span className="text-base font-bold text-white tabular-nums">
        {showCo2 ? co2Str : kwhStr}
      </span>
      <span className="text-xs text-gray-500 tabular-nums">
        {showCo2 ? kwhStr : `${co2Str} CO₂e`}
      </span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function PowerWidget() {
  const [summary, setSummary] = useState<PowerSummary | null>(null);
  const [liveW, setLiveW] = useState<number | null>(null);
  const [chartTab, setChartTab] = useState<"daily" | "monthly">("daily");
  const [showCo2, setShowCo2] = useState(false);
  const wHistory = useRef<number[]>([]);
  const [, forceUpdate] = useState(0);

  // Live current-power every 5 s
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await fetch("/api/system/power");
        const d: CurrentPower = await r.json();
        if (!cancelled) {
          setLiveW(typeof d.total_w === "number" ? d.total_w : null);
          wHistory.current = [...wHistory.current, d.total_w].slice(-60);
          forceUpdate((n) => n + 1);
        }
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Summary every 60 s
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (cancelled) return;
      try {
        const r = await fetch("/api/system/power/summary");
        const d: PowerSummary = await r.json();
        if (!cancelled && d.current) setSummary(d);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const chartBars = chartTab === "daily"
    ? summary?.daily_chart ?? []
    : summary?.monthly_chart ?? [];

  const chartLabelKey = chartTab === "daily" ? "date" : "month";

  return (
    <div className="border-t border-gray-800 pt-6 mt-2 space-y-4">
      {/* Header row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
            ⚡ Systemforbrug
          </span>
          {typeof liveW === "number" && (
            <span className="text-sm font-bold text-yellow-400 tabular-nums">
              {liveW.toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} W
            </span>
          )}
          {typeof liveW !== "number" && (
            <span className="text-xs text-gray-600 animate-pulse">måler…</span>
          )}
        </div>

        {/* CO₂ toggle */}
        <div className="ml-auto flex rounded-lg border border-gray-700 overflow-hidden text-xs">
          <button
            onClick={() => setShowCo2(false)}
            className={`px-2.5 py-1 transition-colors ${!showCo2 ? "bg-yellow-500/20 text-yellow-400 font-medium" : "bg-transparent text-gray-500 hover:text-gray-300"}`}
          >
            kWh
          </button>
          <button
            onClick={() => setShowCo2(true)}
            className={`px-2.5 py-1 border-l border-gray-700 transition-colors ${showCo2 ? "bg-green-500/20 text-green-400 font-medium" : "bg-transparent text-gray-500 hover:text-gray-300"}`}
          >
            CO₂e
          </button>
        </div>
      </div>

      {/* Breakdown of live watts */}
      {summary && (
        <div className="flex gap-2 flex-wrap text-xs text-gray-500">
          <span>🖥️ GPU <span className="text-gray-300 font-medium">{summary.current.gpu_w.toLocaleString("da-DK")} W</span></span>
          <span>· CPU <span className="text-gray-300 font-medium">{summary.current.cpu_w.toLocaleString("da-DK")} W</span> ({summary.current.cpu_pct.toLocaleString("da-DK")}%)</span>
          <span>· Base <span className="text-gray-300 font-medium">{summary.current.base_w} W</span></span>
          <span className="ml-auto text-gray-600">47 g CO₂/kWh · DK 2026</span>
        </div>
      )}

      {/* Stat cards */}
      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="I dag"      kwh={summary.today_kwh}  co2e_g={summary.today_co2e_g}  showCo2={showCo2} />
          <StatCard label="Denne måned" kwh={summary.month_kwh}  co2e_g={summary.month_co2e_g}  showCo2={showCo2} />
          <StatCard label="Dette år"    kwh={summary.year_kwh}   co2e_g={summary.year_co2e_g}   showCo2={showCo2} />
        </div>
      )}
      {!summary && (
        <div className="grid grid-cols-3 gap-3">
          {["I dag", "Denne måned", "Dette år"].map((l) => (
            <div key={l} className="bg-gray-800/40 rounded-xl p-3 h-16 animate-pulse" />
          ))}
        </div>
      )}

      {/* Chart tabs + bar chart */}
      <div className="bg-gray-800/40 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex rounded-lg border border-gray-700 overflow-hidden text-xs">
            {(["daily", "monthly"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setChartTab(t)}
                className={`px-3 py-1 transition-colors ${chartTab === t ? "bg-gray-700 text-white font-medium" : "bg-transparent text-gray-500 hover:text-gray-300"} ${t === "monthly" ? "border-l border-gray-700" : ""}`}
              >
                {t === "daily" ? "30 dage" : "12 måneder"}
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-600">
            {showCo2 ? "CO₂e" : "Elforbrug"}
          </span>
        </div>

        <BarChart
          bars={chartBars}
          labelKey={chartLabelKey as "date" | "month"}
          valueKey={showCo2 ? "co2e_g" : "kwh"}
          color={showCo2 ? "#4ade80" : "#facc15"}
          unit={showCo2 ? "g CO₂e" : "kWh"}
          maxBars={chartTab === "daily" ? 30 : 12}
        />
      </div>
    </div>
  );
}
